import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Trophy, Zap, MapPin, Calendar as CalIcon } from "lucide-react";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/227f0628-5d06-4b29-99e9-ef50d760046a/images/8140291a1d7fe2852df52a079f51e50935b5ed379d25f91d92e91d234780c881.png";
const FEATURE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/227f0628-5d06-4b29-99e9-ef50d760046a/images/12dfbe98315e61af24b9cb3aa188f9f7b4de3ef4600b993e27998f2372bba0c2.png";
const ACTION_IMG =
  "https://images.unsplash.com/photo-1613125479732-14543c793349?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxmcmllbmRzJTIwcGxheWluZyUyMGZvb3RiYWxsfGVufDB8fHx8MTc3OTA0MzgxMHww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#0A0A0A] text-white">
      {/* HERO */}
      <section className="relative h-[100vh] w-full overflow-hidden grain">
        <img
          src={HERO_BG}
          alt="turf"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-20 h-full flex flex-col justify-end">
          <p data-testid="hero-eyebrow" className="text-xs uppercase tracking-[0.4em] text-[#CCFF00] mb-6 fade-in">
            Chhindwara · Open 6 AM – 12 AM
          </p>
          <h1
            data-testid="hero-title"
            className="font-display text-6xl sm:text-7xl lg:text-[9vw] leading-[0.85] tracking-tighter uppercase fade-in"
          >
            Netflick
            <br />
            <span className="kinetic-text">& Chill</span>
          </h1>
          <p className="mt-8 max-w-xl text-base lg:text-lg text-white/70 fade-in">
            Premium 5-a-side turf. 18 real-time slots a day. Grab the ball,
            grab the crew, and own the night.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 fade-in">
            <Link
              to="/book"
              data-testid="hero-cta-book"
              className="inline-flex items-center gap-2 px-7 py-4 bg-[#CCFF00] text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#D4FF33] transition-colors"
            >
              Reserve a Slot <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <Link
              to="/my-bookings"
              data-testid="hero-cta-mybookings"
              className="inline-flex items-center gap-2 px-7 py-4 border border-white/20 text-white text-xs uppercase tracking-[0.25em] font-bold hover:border-white/60 transition-colors"
            >
              Check My Bookings
            </Link>
          </div>

          {/* Bottom strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-white/10 pt-8">
            {[
              { k: "18", v: "Daily Slots" },
              { k: "5v5", v: "Format" },
              { k: "₹600+", v: "Per Hour" },
              { k: "24/7", v: "Online Booking" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-4xl lg:text-5xl text-white">{s.k}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-3">Why us</p>
            <h2 data-testid="features-title" className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter">
              Built for the game
            </h2>
          </div>
          <Link
            to="/book"
            className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold border-b border-[#CCFF00] pb-1 text-[#CCFF00]"
          >
            Book a slot <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-7 row-span-2 relative overflow-hidden h-[400px] md:h-[520px] border border-white/10">
            <img src={FEATURE_IMG} alt="ball" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00]">Pitch 01</p>
              <h3 className="font-display text-3xl md:text-5xl mt-3 uppercase tracking-tight">
                FIFA-grade turf, floodlit nights
              </h3>
              <p className="text-white/70 mt-3 max-w-md">
                Pro-spec synthetic grass with proper drainage. Plays sharp,
                even after the monsoon.
              </p>
            </div>
          </div>

          <div className="md:col-span-5 bg-[#121212] border border-white/10 p-8 flex flex-col justify-between min-h-[250px]">
            <Clock strokeWidth={1.5} className="w-8 h-8 text-[#CCFF00]" />
            <div>
              <h3 className="font-display text-3xl uppercase tracking-tight">6 AM – 12 AM</h3>
              <p className="text-white/60 mt-2 text-sm">
                18 hourly slots every single day. Early bird or night owl — pick your hour.
              </p>
            </div>
          </div>

          <div className="md:col-span-5 bg-[#121212] border border-white/10 p-8 flex flex-col justify-between min-h-[250px]">
            <Zap strokeWidth={1.5} className="w-8 h-8 text-[#CCFF00]" />
            <div>
              <h3 className="font-display text-3xl uppercase tracking-tight">Real-time slots</h3>
              <p className="text-white/60 mt-2 text-sm">
                No phone tag, no double-booking. The grid updates the moment someone books.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {[
            { i: Trophy, t: "Smart pricing", d: "Weekday mornings ₹600 · evenings ₹800 · weekend nights ₹1000. Pay only what the slot is worth." },
            { i: MapPin, t: "Floodlit + covered", d: "Rain or shine, your fixture goes on." },
            { i: CalIcon, t: "Pay at venue", d: "Reserve online. Settle up when you arrive. No card needed." },
          ].map(({ i: Icon, t, d }) => (
            <div key={t} className="bg-[#121212] border border-white/10 p-7 hover:border-[#CCFF00]/40 transition-colors">
              <Icon strokeWidth={1.5} className="w-6 h-6 text-[#CCFF00]" />
              <h4 className="font-display text-2xl mt-5 uppercase tracking-tight">{t}</h4>
              <p className="text-white/60 mt-2 text-sm">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative overflow-hidden border-y border-white/10">
        <img src={ACTION_IMG} alt="action" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-black/50" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#CCFF00] mb-4">Tonight is the night</p>
            <h2 className="font-display text-5xl lg:text-7xl uppercase tracking-tighter leading-none">
              Lock your slot.<br/>Bring your crew.
            </h2>
          </div>
          <div className="md:justify-self-end">
            <Link
              to="/book"
              data-testid="cta-strip-book"
              className="inline-flex items-center gap-3 px-8 py-5 bg-[#CCFF00] text-black text-xs uppercase tracking-[0.3em] font-bold hover:bg-[#D4FF33] transition-colors"
            >
              Book My Slot <ArrowRight className="w-5 h-5" strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      <footer className="max-w-7xl mx-auto px-6 lg:px-10 py-10 text-xs uppercase tracking-[0.25em] text-white/40 flex flex-wrap justify-between gap-4">
        <span>© {new Date().getFullYear()} Netflick & Chill</span>
        <span>Built for the beautiful game.</span>
      </footer>
    </div>
  );
}
